// Notification helpers for the PWA. These fire local notifications through the
// service worker while the app is running (open or backgrounded). True push
// when the app is fully closed is delivered by the server via the SW "push"
// event; the subscribe() helper below wires that up once VAPID keys exist.

export function notificationsSupported() {
  return "serviceWorker" in navigator && "Notification" in window;
}

export function permissionState() {
  return notificationsSupported() ? Notification.permission : "unsupported";
}

export async function requestPermission() {
  if (!notificationsSupported()) return "unsupported";
  return Notification.requestPermission();
}

// Show a notification via the active service worker registration.
export async function notify(title, options = {}) {
  if (permissionState() !== "granted") return false;
  const reg = await navigator.serviceWorker.ready;
  await reg.showNotification(title, {
    icon: "/icons/icon-192.png",
    badge: "/icons/icon-192.png",
    ...options,
  });
  return true;
}

// Register for real Web Push. Needs the server's public VAPID key (exposed at
// build time as VITE_VAPID_PUBLIC_KEY). Returns the PushSubscription or null.
export async function subscribeToPush() {
  const key = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  if (!key || permissionState() !== "granted") return null;
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ||
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    }));
  // Hand the subscription to the server so it can push later.
  await fetch("/api/subscribe", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(sub),
  }).catch(() => {});
  return sub;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}
